import React, { useCallback, useMemo, useState } from "react";
import { Button, Card, Divider, Empty, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, TimePicker, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useScheduleDraftEditorWorkspace from "../../hooks/useScheduleDraftEditorWorkspace";
import { getScheduleDraftTransportDemands } from "../../constants/transportDemandDefaults";
import { getDraftFlights } from "../../lib/trackUtils";
import { normalizeAirportCode } from "../../lib/routeUtils";

const { Text } = Typography;

const DAY_OPTIONS = [
    { label: "周日", value: 0 },
    { label: "周一", value: 1 },
    { label: "周二", value: 2 },
    { label: "周三", value: 3 },
    { label: "周四", value: 4 },
    { label: "周五", value: 5 },
    { label: "周六", value: 6 },
];

const DAY_LABELS = { 0: "日", 1: "一", 2: "二", 3: "三", 4: "四", 5: "五", 6: "六" };

function generateDemandId() {
    return `td-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function buildDemandName(demand) {
    const route = `${demand.departure_airport || "?"} → ${demand.arrival_airport || "?"}`;
    const days = (demand.preferred_days || []).map((d) => DAY_LABELS[d] || d).join(",");
    return days ? `${route} 周${days}` : route;
}

function buildDemandCoverageItems(demands = [], flights = []) {
    return demands.map((demand, index) => {
        const departureAirport = normalizeAirportCode(demand.departure_airport);
        const arrivalAirport = normalizeAirportCode(demand.arrival_airport);
        const matchedFlights = flights.filter((flight) => {
            const flightDeparture = normalizeAirportCode(flight?.departure_airport);
            const flightArrival = normalizeAirportCode(flight?.arrival_airport);
            return flightDeparture === departureAirport && (!arrivalAirport || flightArrival === arrivalAirport);
        });
        const requiredCount = Math.max(Number(demand.frequency || 1), 1);

        return {
            key: demand.id || `${departureAirport}-${arrivalAirport}-${index}`,
            demand,
            matchedCount: matchedFlights.length,
            requiredCount,
            covered: matchedFlights.length >= requiredCount,
        };
    });
}

const ScheduleDraftTransportDemandPanel = () => {
    const {
        draftId,
        data: { draft },
        mutations: { handleUpdateDraft, updatingDraft },
        permissions: { canUpdateFlight },
    } = useScheduleDraftEditorWorkspace();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingDemand, setEditingDemand] = useState(null);
    const [form] = Form.useForm();

    const demands = useMemo(() => getScheduleDraftTransportDemands(draft), [draft]);
    const coverageItems = useMemo(() => buildDemandCoverageItems(demands, getDraftFlights(draft)), [demands, draft]);

    const persistDemands = useCallback(
        (nextDemands) => {
            if (!draftId) return;
            handleUpdateDraft({ transport_demands: nextDemands });
        },
        [draftId, handleUpdateDraft],
    );

    const openAddModal = useCallback(() => {
        setEditingDemand(null);
        form.resetFields();
        setModalOpen(true);
    }, [form]);

    const openEditModal = useCallback(
        (demand) => {
            setEditingDemand(demand);
            form.setFieldsValue({
                departure_airport: demand.departure_airport,
                arrival_airport: demand.arrival_airport,
                preferred_days: demand.preferred_days || [],
                preferred_time: demand.preferred_time ? dayjs(demand.preferred_time, "HH:mm") : null,
                frequency: demand.frequency ?? 1,
                demand_name: demand.demand_name || "",
            });
            setModalOpen(true);
        },
        [form],
    );

    const handleDeleteDemand = useCallback(
        (demandId) => {
            const next = demands.filter((d) => d.id !== demandId);
            persistDemands(next);
        },
        [demands, persistDemands],
    );

    const handleModalOk = useCallback(async () => {
        try {
            const values = await form.validateFields();
            const demandData = {
                id: editingDemand?.id || generateDemandId(),
                departure_airport: (values.departure_airport || "").toUpperCase().trim(),
                arrival_airport: (values.arrival_airport || "").toUpperCase().trim(),
                preferred_days: values.preferred_days || [],
                preferred_time: values.preferred_time ? values.preferred_time.format("HH:mm") : null,
                frequency: values.frequency ?? 1,
                demand_name: values.demand_name?.trim() || "",
            };

            if (!demandData.demand_name) {
                demandData.demand_name = buildDemandName(demandData);
            }

            let nextDemands;
            if (editingDemand) {
                nextDemands = demands.map((d) => (d.id === editingDemand.id ? demandData : d));
            } else {
                nextDemands = [...demands, demandData];
            }

            persistDemands(nextDemands);
            setModalOpen(false);
            form.resetFields();
            setEditingDemand(null);
        } catch {
            // validation error, form handles display
        }
    }, [demands, editingDemand, form, persistDemands]);

    const handleModalCancel = useCallback(() => {
        setModalOpen(false);
        form.resetFields();
        setEditingDemand(null);
    }, [form]);

    const columns = useMemo(
        () => [
            {
                title: "航线",
                key: "route",
                width: 120,
                render: (_, record) => (
                    <Text strong>
                        {record.departure_airport} → {record.arrival_airport}
                    </Text>
                ),
            },
            {
                title: "班期",
                key: "days",
                width: 130,
                render: (_, record) => (
                    <Space size={2} wrap>
                        {(record.preferred_days || []).map((d) => (
                            <Tag key={d} color="blue" style={{ margin: 0 }}>
                                {DAY_LABELS[d] || d}
                            </Tag>
                        ))}
                    </Space>
                ),
            },
            {
                title: "首发",
                dataIndex: "preferred_time",
                width: 60,
                render: (val) => val || "-",
            },
            {
                title: "",
                key: "actions",
                width: 60,
                render: (_, record) =>
                    canUpdateFlight ? (
                        <Space size={4}>
                            <Tooltip title="编辑">
                                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
                            </Tooltip>
                            <Tooltip title="删除">
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteDemand(record.id)} />
                            </Tooltip>
                        </Space>
                    ) : null,
            },
        ],
        [canUpdateFlight, handleDeleteDemand, openEditModal],
    );

    return (
        <Card
            title="需求与覆盖"
            className="schedule-draft-side-card"
            size="small"
            extra={
                canUpdateFlight ? (
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddModal}>
                        新增
                    </Button>
                ) : null
            }
        >
            {demands.length > 0 ? (
                <Table
                    dataSource={demands}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    className="schedule-draft-demand-table"
                    loading={updatingDraft}
                />
            ) : (
                <Empty description="暂无运输需求" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                    {canUpdateFlight ? (
                        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                            添加运输需求
                        </Button>
                    ) : null}
                </Empty>
            )}

            <Divider style={{ margin: "12px 0 8px" }} />

            <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Text strong>覆盖关系</Text>
                {coverageItems.length ? (
                    <div className="schedule-draft-demand-coverage-list">
                        {coverageItems.map((item) => (
                            <div key={item.key} className="schedule-draft-demand-coverage-row">
                                <Space wrap size={[6, 6]}>
                                    <Tag color={item.covered ? "green" : "orange"}>{item.covered ? "已覆盖" : "待覆盖"}</Tag>
                                    <Text strong>
                                        {item.demand.departure_airport || "?"} → {item.demand.arrival_airport || "?"}
                                    </Text>
                                </Space>
                                <Text type="secondary">{item.matchedCount} / {item.requiredCount} 个匹配航班</Text>
                            </div>
                        ))}
                    </div>
                ) : (
                    <Empty description="暂无可计算的需求覆盖关系" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Space>

            <Modal
                title={editingDemand ? "编辑运输需求" : "新增运输需求"}
                open={modalOpen}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={editingDemand ? "保存" : "添加"}
                cancelText="取消"
                confirmLoading={updatingDraft}
                destroyOnClose
                width={480}
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item name="demand_name" label="需求名称">
                        <Input placeholder="留空自动生成（如 HKG → LGG 周一三五）" maxLength={100} />
                    </Form.Item>

                    <Space size={12} style={{ width: "100%" }}>
                        <Form.Item
                            name="departure_airport"
                            label="出发机场"
                            rules={[{ required: true, message: "请输入出发机场 IATA 代码" }]}
                            style={{ flex: 1 }}
                        >
                            <Input placeholder="HKG" maxLength={4} style={{ textTransform: "uppercase" }} />
                        </Form.Item>

                        <Form.Item
                            name="arrival_airport"
                            label="目的机场"
                            rules={[{ required: true, message: "请输入目的机场 IATA 代码" }]}
                            style={{ flex: 1 }}
                        >
                            <Input placeholder="LGG" maxLength={4} style={{ textTransform: "uppercase" }} />
                        </Form.Item>
                    </Space>

                    <Form.Item name="preferred_days" label="班期（星期几执行）">
                        <Select mode="multiple" options={DAY_OPTIONS} placeholder="选择执行日期" allowClear />
                    </Form.Item>

                    <Space size={12} style={{ width: "100%" }}>
                        <Form.Item name="preferred_time" label="首选出发时间" style={{ flex: 1 }}>
                            <TimePicker format="HH:mm" style={{ width: "100%" }} placeholder="08:00" />
                        </Form.Item>

                        <Form.Item name="frequency" label="周期频次" style={{ flex: 1 }}>
                            <InputNumber min={1} max={14} placeholder="1" style={{ width: "100%" }} />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </Card>
    );
};

export default ScheduleDraftTransportDemandPanel;
